import { ReactNode, useState } from 'react';

import { Menu } from 'lucide-react';
import { Button } from '@/components/ui/button';

import { PortalSidebar } from './PortalSidebar';
import { PortalUserMenu } from './PortalUserMenu';
import { PortalInvestorSwitcher } from './PortalInvestorSwitcher';
import { ImpersonationBanner } from './ImpersonationBanner';

interface PortalLayoutProps {
  children: ReactNode;
}

function TopHeader({ onMenuClick }: { onMenuClick: () => void }) {
  return (
    <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-border bg-card px-4 lg:px-6">
      {/* Left side - Mobile menu + Investor info */}
      <div className="flex items-center gap-4">
        {/* Mobile hamburger */}
        <Button
          variant="ghost"
          size="icon"
          onClick={onMenuClick}
          className="lg:hidden"
        >
          <Menu className="h-6 w-6" />
          <span className="sr-only">Open menu</span>
        </Button>

        {/* Mobile logo */}
        <div className="flex lg:hidden items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-blue-600 to-indigo-600">
            <span className="text-sm font-bold text-white">DFF</span>
          </div>
        </div>

        {/* Investor name with switcher */}
        <PortalInvestorSwitcher />
      </div>

      {/* Right side - User menu */}
      <PortalUserMenu />
    </header>
  );
}

export function PortalLayout({ children }: PortalLayoutProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  return (
    <div className="flex flex-col h-dvh bg-background">
      {/* Impersonation Banner - shown at very top when admin is impersonating */}
      <ImpersonationBanner />

      <div className="flex flex-1 min-h-0">
        {/* Sidebar - Navigation only */}
        <PortalSidebar
          isMobileMenuOpen={isMobileMenuOpen}
          onClose={() => setIsMobileMenuOpen(false)}
        />

        {/* Main content area */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          {/* Top header - Investor & User info */}
          <TopHeader onMenuClick={() => setIsMobileMenuOpen(true)} />

          {/* Relative, so absolute sr-only labels scroll here instead of
              growing the window into a second scrollbar. */}
          <main className="relative flex-1 overflow-y-auto p-4 lg:p-6">
            {children}
          </main>
        </div>
      </div>
    </div>
  );
}
